import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Users, FileText, DollarSign, Calendar, AlertCircle, CheckCircle, CheckCircle2,
  Building2, Clock, TrendingUp, Shield, Heart, UserCheck, 
  CalendarDays, Briefcase, UserPlus, Settings, PieChart,
  Activity, Target, Award, Banknote, MapPin, Sparkles, X, Play
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { format, addDays, startOfMonth, endOfMonth, differenceInCalendarDays, isAfter } from "date-fns";
import { useMemo } from "react";
import { Link, useLocation } from "wouter";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import { useEmployerTour } from "@/components/tours/EmployerTourManager";
import { parseDateLocal } from "@/lib/utils";
import { countByDepartment, departmentLabel } from "@/lib/departmentLabel";
import { formatStatus } from "@/lib/employeeStatus";
import { pluralize } from "@/lib/pluralize";

export default function EmployerDashboard() {
  const [, setLocation] = useLocation();
  const { shouldShowWelcome, startTour, dismissWelcome } = useEmployerTour();

  // Get authenticated user to read clientId for scoping
  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/user"] });

  // Client-scoped Employees
  const { data: employeesData = [] } = useQuery<any[]>({
    queryKey: [user?.clientId ? `/api/employees` : ""],
    enabled: !!user?.clientId,
  });

  const employees = useMemo(() => {
    return sortEmployeesByFullName(employeesData);
  }, [employeesData]);

  // Client-scoped Enrollments
  const { data: enrollments = [] } = useQuery<any[]>({
    queryKey: [user?.clientId ? `/api/enrollments` : ""],
    enabled: !!user?.clientId,
  });

  // Client-scoped Plans (via client-plans)
  const { data: plans = [] } = useQuery<any[]>({
    queryKey: [user?.clientId ? `/api/client-plans` : ""],
    enabled: !!user?.clientId,
  });

  // Open Enrollment window from the dedicated OE table
  const { data: oeWindow } = useQuery<{
    id: number;
    clientId: number;
    startDate: string;
    endDate: string;
  } | null>({
    queryKey: [user?.clientId ? `/api/clients/${user.clientId}/open-enrollment` : ""],
    enabled: !!user?.clientId,
  });

  const hasActiveOeWindow = useMemo(() => {
    if (!oeWindow?.startDate || !oeWindow?.endDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = parseDateLocal(oeWindow.startDate);
    const end = parseDateLocal(oeWindow.endDate);
    if (!start || !end) return false;
    return today >= start && today <= end;
  }, [oeWindow]);

  // OE progress rows — only fetch when there's an active OE window
  const { data: oeProgress = [] } = useQuery<{
    employeeId: number;
    status: string;
  }[]>({
    queryKey: [`/api/clients/${user?.clientId}/open-enrollment/progress`, oeWindow?.startDate],
    queryFn: async () => {
      const res = await fetch(
        `/api/clients/${user?.clientId}/open-enrollment/progress?startDate=${oeWindow?.startDate}`,
        { credentials: "include" }
      );
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!user?.clientId && hasActiveOeWindow && !!oeWindow?.startDate,
  });

  // Calculate comprehensive metrics
  const activeEmployees = employees.filter((emp: any) => emp.status === "active").length;
  // ONB-14: onboarding completion counted across active employees only —
  // terminated/inactive shouldn't dilute the rate.
  const activeEmployeeList = employees.filter((emp: any) => emp.status === "active");
  const onboardingCompletedCount = activeEmployeeList.filter(
    (emp: any) => emp.onboardingCompletedAt != null,
  ).length;
  const onboardingCompletionRate =
    activeEmployees > 0
      ? Math.round((onboardingCompletedCount / activeEmployees) * 100)
      : 0;
  const onboardingIncompleteCount = activeEmployees - onboardingCompletedCount;
  const employmentTypeMissingCount = activeEmployeeList.filter(
    (emp: any) => !emp.employmentType || !String(emp.employmentType).trim()
  ).length;
  const pendingEnrollments = enrollments.filter(
    (enr: any) => (enr.status ?? "active") === "pending"
  ).length;
  const activeNonWaivedEnrollments = enrollments.filter((enr: any) => {
    const isActive = (enr.status ?? "active") === "active";
    const notWaived = (enr.waivedCoverage ?? false) !== true;
    const notEnded = !enr.endDate || (parseDateLocal(enr.endDate) ?? new Date(0)) > new Date();
    return isActive && notWaived && notEnded;
  });
  const historicalEnrolledCount = Array.from(
    new Set(activeNonWaivedEnrollments.map((e: any) => e.employeeId))
  ).length;
  const historicalEnrollmentRate =
    activeEmployees > 0
      ? Math.round((historicalEnrolledCount / activeEmployees) * 100)
      : 0;

  // During active OE, show OE completion stats instead of historical enrollment
  const oeCompletedCount = hasActiveOeWindow
    ? oeProgress.filter((p) => p.status === "complete").length
    : 0;
  const enrolledEmployeeCount = hasActiveOeWindow ? oeCompletedCount : historicalEnrolledCount;
  const enrollmentRate = hasActiveOeWindow
    ? (activeEmployees > 0 ? Math.round((oeCompletedCount / activeEmployees) * 100) : 0)
    : historicalEnrollmentRate;

  // Department breakdown — current staff only (non-terminated), so %s sum to ~100.
  const departmentPopulation = employees.filter((emp: any) => emp.status !== "terminated");
  const departmentStats = countByDepartment(departmentPopulation);

  // Employer-scoped PTO pending requests count
  const { data: ptoCountData } = useQuery<any>({
    queryKey: [user?.clientId ? `/api/time-off/requests/count?status=pending` : ""],
    enabled: !!user?.clientId,
  });
  const ptoRequests = ptoCountData?.count ?? 0;

  // Employer-scoped PTO approved requests count (for Auto-Approved)
  const { data: ptoApprovedData } = useQuery<any>({
    queryKey: [user?.clientId ? `/api/time-off/requests/count?status=approved` : ""],
    enabled: !!user?.clientId,
  });
  const ptoApproved = ptoApprovedData?.count ?? 0;
  const contractorsCount = employees.filter((e: any) => {
    const type = (e.employmentType || "").toString().toLowerCase();
    return (
      e.status === "active" &&
      (type.includes("contract") || type === "contractor")
    );
  }).length;
  // Compliance alerts: count client compliance docs not approved
  const { data: complianceDocsCount } = useQuery<any>({
    queryKey: [
      user?.clientId
        ? `/api/documents/count?ownerType=client&status=active&approved=false&category=compliance`
        : "",
    ],
    enabled: !!user?.clientId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const complianceAlerts = complianceDocsCount?.count ?? 0;

  // Compliance checklist summary for accurate score calculation
  const { data: checklistSummary } = useQuery<Record<string, { total: number; completed: number; isComplete: boolean }>>({
    queryKey: ["/api/compliance/checklist-summary", user?.clientId],
    queryFn: async () => {
      if (!user?.clientId) return {};
      const res = await fetch(`/api/compliance/checklist-summary?clientId=${user.clientId}`, { credentials: 'include' });
      if (!res.ok) return {};
      return res.json();
    },
    enabled: !!user?.clientId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Calculate actual compliance score from checklists
  const complianceScore = (() => {
    if (!checklistSummary || Object.keys(checklistSummary).length === 0) {
      return 100; // No checklists = assume compliant
    }
    let totalItems = 0;
    let completedItems = 0;
    for (const area of Object.values(checklistSummary)) {
      totalItems += area.total;
      completedItems += area.completed;
    }
    if (totalItems === 0) return 100;
    return Math.round((completedItems / totalItems) * 100);
  })();

  const incompleteChecklistItems = (() => {
    if (!checklistSummary) return 0;
    let total = 0;
    let completed = 0;
    for (const area of Object.values(checklistSummary)) {
      total += area.total;
      completed += area.completed;
    }
    return total - completed;
  })();
  // Payroll connections
  const { data: payrollConnections = [] } = useQuery<
    Array<{ status: string; lastSyncedAt: string | null }>
  >({
    queryKey: [user?.clientId ? `/api/clients/${user.clientId}/payroll-connections` : ""],
    enabled: !!user?.clientId,
  });
  const payrollIntegrations = payrollConnections.filter(
    (c) => c.status === "connected" || c.status === "syncing",
  ).length;
  const payrollLastSync = payrollConnections.reduce<Date | null>((latest, c) => {
    if (!c.lastSyncedAt) return latest;
    const d = new Date(c.lastSyncedAt);
    return !latest || d > latest ? d : latest;
  }, null);
  // Client-scoped documents to review (active and not yet approved)
  const { data: docsCountData } = useQuery<any>({
    queryKey: [
      user?.clientId
        ? `/api/documents/count?ownerType=client&status=active&approved=false`
        : "",
    ],
    enabled: !!user?.clientId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const documentsToReview = docsCountData?.count ?? 0;

  // Compliance documents expiring soon (next 30 days)
  // Important: keep this value stable across renders to avoid constantly changing query keys.
  // We memoize it per-day so it updates daily, not every render (which caused refetch loops).
  const todayKey = format(new Date(), "yyyy-MM-dd");
  const next30 = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return addDays(today, 30).toISOString();
  }, [todayKey]);
  const { data: expiringComplianceDocs } = useQuery<any>({
    queryKey: [
      user?.clientId
        ? `/api/documents/count?ownerType=client&status=active&category=compliance&before=${encodeURIComponent(
            next30
          )}`
        : "",
    ],
    enabled: !!user?.clientId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const expiringSoonCount = expiringComplianceDocs?.count ?? 0;

  // Hires this month (runner-up improvement for Total Employees card)
  const monthStart = startOfMonth(new Date());
  const monthEnd = endOfMonth(new Date());
  const hiresThisMonth = employees.filter((emp: any) => {
    if (!emp.hireDate) return false;
    const d = parseDateLocal(emp.hireDate);
    if (!d) return false;
    return d >= monthStart && d <= monthEnd;
  }).length;

  // Open Enrollment window based on client plans
  const now = new Date();
  const futureEffectiveDates = plans
    .map((p: any) => (p?.effectiveDate ? parseDateLocal(p.effectiveDate) : null))
    .filter((d: any) => d && isAfter(d, now)) as Date[];
  const nearestEffective = futureEffectiveDates.sort(
    (a, b) => a.getTime() - b.getTime()
  )[0];
  // Fallback to plan endDate if effectiveDate isn't available
  const futureEndDates = plans
    .map((p: any) => (p?.endDate ? parseDateLocal(p.endDate) : null))
    .filter((d: any) => d && isAfter(d, now)) as Date[];
  const nearestEnd = futureEndDates.sort(
    (a, b) => a.getTime() - b.getTime()
  )[0];
  const oeEnd = nearestEffective || nearestEnd;
  const oeStart = oeEnd ? addDays(oeEnd, -30) : undefined;
  const isOpenEnrollmentActive =
    oeStart && oeEnd ? now >= oeStart && now <= oeEnd : false;
  const oeDaysRemaining = oeEnd ? differenceInCalendarDays(oeEnd, now) : 0;

  // New Hire Enrollment Windows
  const { data: newHireWindows = [] } = useQuery<any[]>({
    queryKey: [user?.clientId ? `/api/clients/${user.clientId}/new-hire-windows` : ""],
    enabled: !!user?.clientId,
    queryFn: async () => {
      const res = await fetch(`/api/clients/${user?.clientId}/new-hire-windows`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.employees || [];
    },
  });

  // Count new hires by status
  const newHireStats = useMemo(() => {
    const open = newHireWindows.filter((e: any) => e.isWindowOpen && e.status !== "completed").length;
    const urgent = newHireWindows.filter((e: any) => e.isWindowOpen && e.daysRemaining <= 7 && e.status !== "completed").length;
    const expired = newHireWindows.filter((e: any) => (e.isExpired || e.status === "expired") && e.status !== "completed").length;
    return { open, urgent, expired };
  }, [newHireWindows]);

  // Get company info from user profile
  const { data: userProfile } = useQuery<{ 
    clientName?: string; 
    clientLogo?: string;
    firstName?: string;
  }>({
    queryKey: ["/api/user/profile"],
    enabled: !!user,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {userProfile?.clientLogo && (
            <img
              src={userProfile.clientLogo}
              alt={userProfile.clientName || "Company"}
              className="h-16 w-auto max-w-[160px] object-contain rounded-lg border bg-white p-2"
            />
          )}
          <div>
            <h1 className="text-3xl font-bold">
              HR Dashboard{userProfile?.clientName ? ` for ${userProfile.clientName}` : ""}
            </h1>
            <p className="text-muted-foreground">
              Welcome back{userProfile?.firstName ? `, ${userProfile.firstName}` : ""}! Here's what's happening with your team.
            </p>
          </div>
        </div>
      </div>

      {/* Welcome Banner for New Employers */}
      {shouldShowWelcome && (
        <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-primary/15 p-3">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Welcome! Let's set up your company.
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Take a quick tour to learn where everything is — your employees, benefits, compliance tools, and more.
                  </p>
                  <div className="flex gap-3">
                    <Button onClick={startTour}>
                      <Play className="h-4 w-4 mr-2" />
                      Start Tour
                    </Button>
                    <Button variant="outline" onClick={dismissWelcome}>
                      Maybe Later
                    </Button>
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={dismissWelcome}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Primary Metrics - Colorful Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-blue-500 bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/20 dark:to-blue-900/10">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Active Employees
            </CardTitle>
            <Users className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-800 dark:text-blue-200">
              {activeEmployees}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <TrendingUp className="h-3 w-3 text-green-500" />
              <p className="text-xs text-blue-600 dark:text-blue-400">
                +{hiresThisMonth} hired this month
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500 bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-950/20 dark:to-green-900/10">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-green-700 dark:text-green-300">
              {hasActiveOeWindow ? "Open Enrollment" : "Benefits Enrollment"}
            </CardTitle>
            <Heart className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-800 dark:text-green-200">
              {enrollmentRate}%
            </div>
            <div className="mt-2">
              <Progress
                value={enrollmentRate}
                className="h-2 bg-green-100 dark:bg-green-900/20"
              />
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                {enrolledEmployeeCount} of {activeEmployees} {hasActiveOeWindow ? "completed" : "enrolled"}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500 bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-950/20 dark:to-orange-900/10">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-orange-700 dark:text-orange-300">
              Pending Actions
            </CardTitle>
            <AlertCircle className="h-5 w-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-800 dark:text-orange-200">
              {pendingEnrollments + ptoRequests + documentsToReview}
            </div>
            <p className="text-xs text-orange-600 dark:text-orange-400">
              {pendingEnrollments} {pluralize(pendingEnrollments, "enrollment")} • {ptoRequests} PTO •{" "}
              {documentsToReview} docs
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500 bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-950/20 dark:to-purple-900/10">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-purple-700 dark:text-purple-300">
              Compliance Score
            </CardTitle>
            <Shield className="h-5 w-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-800 dark:text-purple-200">
              {complianceScore}%
            </div>
            <div className="mt-2">
              <Progress
                value={complianceScore}
                className="h-2 bg-purple-100 dark:bg-purple-900/20"
              />
              <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                {incompleteChecklistItems > 0 
                  ? `${incompleteChecklistItems} items incomplete`
                  : "All items complete"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ONB-14: Onboarding completion across active employees */}
      <Card className="border-l-4 border-l-teal-500">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-1 min-w-[240px]">
              <UserCheck className="h-5 w-5 text-teal-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">Onboarding completion</p>
                <p className="text-xs text-muted-foreground">
                  {onboardingCompletedCount} of {activeEmployees} active employee
                  {activeEmployees === 1 ? "" : "s"} have completed onboarding
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-1 min-w-[240px]">
              <Progress value={onboardingCompletionRate} className="h-2 flex-1" />
              <span className="text-sm font-semibold text-teal-700 dark:text-teal-300 w-12 text-right">
                {onboardingCompletionRate}%
              </span>
            </div>
            {onboardingIncompleteCount > 0 && (
              <Link href="/employees?filter=onboarding-incomplete">
                <Button variant="outline" size="sm">
                  View {onboardingIncompleteCount} incomplete
                </Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>

      {employmentTypeMissingCount > 0 && (
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-6 pb-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-1 min-w-[240px]">
                <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Employment type missing</p>
                  <p className="text-xs text-muted-foreground">
                    {employmentTypeMissingCount} active employee
                    {employmentTypeMissingCount === 1 ? "" : "s"} have no employment type on file
                  </p>
                </div>
              </div>
              <Link href="/employees?filter=employment-type-missing">
                <Button variant="outline" size="sm">
                  View {employmentTypeMissingCount} missing
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feature Overview Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-cyan-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Employee Management
            </CardTitle>
            <UserCheck className="h-5 w-5 text-cyan-600" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Active Employees</span>
                <Badge
                  variant="default"
                  className="bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300 hover:bg-blue-500 hover:text-white"
                >
                  {activeEmployees}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Departments</span>
                <Badge variant="outline">
                  {Object.keys(departmentStats).length}
                </Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() => setLocation("/employees?role=employer")}
              >
                <Users className="h-4 w-4 mr-2" />
                Manage Employees
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* New Hire Enrollments Card */}
        <Card className={`hover:shadow-lg transition-shadow border-l-4 ${newHireStats.urgent > 0 ? 'border-l-amber-500' : 'border-l-teal-500'}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              New Hire Enrollments
            </CardTitle>
            <Clock className={`h-5 w-5 ${newHireStats.urgent > 0 ? 'text-amber-600' : 'text-teal-600'}`} />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Open Windows</span>
                <Badge
                  variant="default"
                  className={newHireStats.open > 0
                    ? "bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300 hover:bg-blue-500 hover:text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-blue-500 hover:text-white"
                  }
                >
                  {newHireStats.open}
                </Badge>
              </div>
              {newHireStats.urgent > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-amber-600">Urgent (≤7 days)</span>
                  <Badge variant="destructive">
                    {newHireStats.urgent}
                  </Badge>
                </div>
              )}
              {newHireStats.expired > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-red-600">Expired</span>
                  <Badge variant="outline" className="border-red-300 text-red-600">
                    {newHireStats.expired}
                  </Badge>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() => setLocation("/employees?role=employer")}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                View New Hires
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-emerald-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Time Off Management
            </CardTitle>
            <CalendarDays className="h-5 w-5 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Pending Requests</span>
                <Badge
                  variant="secondary"
                  className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 hover:bg-blue-500 hover:text-white"
                >
                  {ptoRequests}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Auto-Approved</span>
                <Badge
                  variant="default"
                  className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 hover:bg-blue-500 hover:text-white"
                >
                  {ptoApproved}
                </Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() => setLocation("/time-off?role=employer")}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Review Requests
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-indigo-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Payroll Integration
            </CardTitle>
            <Banknote className="h-5 w-5 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Active Integrations</span>
                <Badge
                  variant="default"
                  className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300 hover:bg-blue-500 hover:text-white"
                >
                  {payrollIntegrations}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Last Sync</span>
                <span className="text-xs text-muted-foreground">
                  {payrollLastSync
                    ? `${Math.max(
                        1,
                        Math.round(
                          (Date.now() - payrollLastSync.getTime()) / (1000 * 60)
                        )
                      )} min ago`
                    : "—"}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() => setLocation("/payroll?role=employer")}
              >
                <DollarSign className="h-4 w-4 mr-2" />
                View Payroll
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-violet-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Benefits & Plans
            </CardTitle>
            <Heart className="h-5 w-5 text-violet-600" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Available Plans</span>
                <Badge
                  variant="default"
                  className="bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300 hover:bg-blue-500 hover:text-white"
                >
                  {plans.length}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Enrollment Rate</span>
                <span className="text-sm font-semibold text-violet-600">
                  {enrollmentRate}%
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() => setLocation("/benefits?role=employer")}
              >
                <Settings className="h-4 w-4 mr-2" />
                Manage Benefits
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-red-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Compliance & HR
            </CardTitle>
            <Shield className="h-5 w-5 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">ACA Compliance</span>
                {complianceAlerts === 0 ? (
                  <Badge
                    variant="default"
                    className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 hover:bg-blue-500 hover:text-white"
                  >
                    ✓
                  </Badge>
                ) : (
                  <Badge variant="destructive">Review</Badge>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">COBRA Alerts</span>
                <Badge variant="secondary">{complianceAlerts}</Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() => setLocation("/hr?role=employer")}
              >
                <FileText className="h-4 w-4 mr-2" />
                View HR Tools
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Department Overview */}
      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Department Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(departmentStats).map(
                ([dept, count]: [string, any]) => {
                  const percentage = Math.round(
                    (count / departmentPopulation.length) * 100
                  );
                  const colors = {
                    Engineering: "bg-blue-500",
                    Marketing: "bg-green-500",
                    Sales: "bg-purple-500",
                    Operations: "bg-orange-500",
                    Finance: "bg-red-500",
                    HR: "bg-pink-500",
                    "Customer Success": "bg-cyan-500",
                  };
                  const color =
                    colors[dept as keyof typeof colors] || "bg-gray-500";

                  return (
                    <div key={dept} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{dept}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            {count} {pluralize(count, "employee")}
                          </span>
                          <Badge variant="outline">{percentage}%</Badge>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`${color} h-2 rounded-full transition-all duration-300`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-green-600" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {employees.slice(0, 5).map((employee: any) => (
                <div
                  key={employee.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {employee.firstName[0]}
                      {employee.lastName[0]}
                    </div>
                    <div>
                      <p className="font-medium">
                        {employee.firstName} {employee.lastName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {departmentLabel(employee.department)} •{" "}
                        {employee.position || "No Title"}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant={
                      employee.status === "active" ? "default" : "secondary"
                    }
                  >
                    {formatStatus(employee.status)}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-orange-600" />
              Priority Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {complianceAlerts > 0 && (
                <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <div className="flex-1">
                    <p className="font-medium text-red-700 dark:text-red-300">
                      COBRA Compliance
                    </p>
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {complianceAlerts} {complianceAlerts === 1 ? "employee needs" : "employees need"} COBRA notifications
                    </p>
                  </div>
                  <Badge variant="destructive">High</Badge>
                </div>
              )}
              {isOpenEnrollmentActive && (
                <div className="flex items-center gap-3 p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                  <Clock className="h-5 w-5 text-orange-500" />
                  <div className="flex-1">
                    <p className="font-medium text-orange-700 dark:text-orange-300">
                      Open Enrollment
                    </p>
                    <p className="text-sm text-orange-600 dark:text-orange-400">
                      Ends in {oeDaysRemaining} {pluralize(oeDaysRemaining, "day")} -{" "}
                      {activeEmployees - enrolledEmployeeCount} pending
                    </p>
                  </div>
                  <Badge variant="secondary">Medium</Badge>
                </div>
              )}
              {complianceAlerts === 0 && !isOpenEnrollmentActive && (
                <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 mb-2 text-green-500" />
                  <p className="font-medium">All caught up!</p>
                  <p className="text-sm">No priority tasks right now</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
